import { memo, useEffect, useState, useMemo, FC } from "react";
import { useCompanyId } from "@store/company/company-store";
import { useSearch } from "@hooks/useSearch";
import { useUnit } from "effector-react";
import { useSearchByName } from "@hooks/useSearchByName";
import { useDepartmentListQuery } from "@lib/departments";
import { useChoiceState } from "@hooks/useChoiceState";
import { useParams } from "react-router-dom";
import useModal from "@modals/modal-hook";
import {
  useDeleteTraineesMembersMutation,
  useDeleteProcessMemberMutation,
  useTraineesMemberToProcessMutation,
  useProcessMembersToFinishMutation,
  useDownloadFileXmlMutation,
  useDownloadFileProtocolMutation,
} from "@lib/study-center";
import { openFile } from "@utils/openFile";
import { InternalTrainingSteps } from "@interfaces/store/internal-list-store-types";
import { ListShortDataT } from "@interfaces/internal-study/list";
import {
  $InternalInfoState,
  $InternalInfo,
  fetchInternalInfo,
  updateInternalList,
  setInternalInfo,
} from "@store/internal-study/list-store";
import {
  $Comissions,
  fetchComissions,
} from "@store/internal-study/comissions-store";
import {
  TextField,
  MenuItem,
  Grid2 as Grid,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Link,
  Tooltip,
} from "@mui/material";
import { XIcon } from "@phosphor-icons/react";
import { Button } from "@ui/button";
import { DropDownLabel } from "@ui/drop-down";
import { Loader } from "@ui/indicators";
import { Search } from "@ui/search";
import { Box, Text } from "@mantine/core";
import clsx from "clsx";
import { BackButton } from "@components/back-button";
import { CheckboxCheckedSVG, CheckboxSVG } from "@components/svgs";
import toast from "react-hot-toast";
import styles from "@scss/modals/company/create-task.module.scss";
import style from "./list-page.module.scss";
import moment from "moment";
import { downloadBlob } from "@utils/downloadBlob";

const SELECT_ALL = {
  id: 0,
  title: "Выбрать все",
};

type InternalListPageProps = {
  setTitle?: (value: string) => void;
};

const InternalListPage: FC<InternalListPageProps> = memo(({ setTitle }) => {
  const { id } = useParams<{ id: string }>();
  const { isLoading, isFetched } = useUnit($InternalInfoState);
  const internalInfo = useUnit($InternalInfo);
  const companyId = useCompanyId();
  const comissions = useUnit($Comissions);
  const { open } = useModal();

  const traineesMemberToProcessMutation = useTraineesMemberToProcessMutation();
  const processMembersToFinishMutation = useProcessMembersToFinishMutation();
  const deleteTraineesMembersMutation = useDeleteTraineesMembersMutation();
  const deleteProcessMemberMutation = useDeleteProcessMemberMutation();
  const downloadFileXmlMutation = useDownloadFileXmlMutation();
  const downloadFileProtocolMutation = useDownloadFileProtocolMutation();

  const { data: departments } = useDepartmentListQuery();

  const [selectedDepartment, setSelectedDepartment] = useState(SELECT_ALL.id);

  const search = useSearch();
  const filteredEmployees = useSearchByName(
    search.debounced,
    internalInfo.trainees_members
  );

  const departmentOptions = [
    SELECT_ALL,
    ...(departments?.map((department) => ({
      id: department.id,
      title: department.title,
    })) ?? []),
  ];

  const {
    selectedItems,
    areAllItemsSelected,
    selectAll,
    clearSelectedItems,
    getIsItemSelected,
    rerender,
  } = useChoiceState(
    internalInfo.trainees_members !== undefined
      ? internalInfo.trainees_members
      : [],
    (employee) => employee.id
  );

  const employeeOptions = useMemo(() => {
    const options = [];
    for (const [index, employee] of filteredEmployees?.entries() ??
      [].entries()) {
      if (
        selectedDepartment !== SELECT_ALL.id &&
        employee.department !==
          departments?.find((e) => e.id === selectedDepartment)?.title
      ) {
        continue;
      }
      employee.protocol_number = `ЛК${moment().format("YYYYMMDD")}-${
        internalInfo.id
      }-${
        index +
        1 +
        (internalInfo.process_members?.reduce(
          (p, e) => p + e.members.length,
          0
        ) ?? 0) +
        (internalInfo.finish_members?.reduce(
          (p, e) => p + e.groups.reduce((gp, g) => gp + g.members.length, 0),
          0
        ) ?? 0)
      }`;
      employee.programs = employee.programs.map((e) => ({
        ...e,
        checked: false,
      }));

      options.push(employee);
    }

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEmployees, selectedDepartment]);

  useEffect(() => {
    if (!isFetched) {
      fetchInternalInfo({ id: id!, companyId });
      if (companyId !== null) {
        fetchComissions(companyId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFetched && setTitle) setTitle(internalInfo.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  const handleProcessRequestCreate = () => {
    traineesMemberToProcessMutation.mutate(
      {
        id: id!,
        from: selectedItems.filter((e) =>
          internalInfo.trainees_members.map((t) => t.id).includes(e.id)
        ),
        to: InternalTrainingSteps.Process,
      },
      {
        onSuccess: (data) => {
          setInternalInfo(data as ListShortDataT);
          clearSelectedItems();
        },
      }
    );
  };

  const handleFinishRequestCreate = async () => {
    const file = await openFile({
      accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    if (!file) return;

    processMembersToFinishMutation.mutate(
      {
        id: id!,
        file: file,
      },
      {
        onSuccess: (data) => {
          setInternalInfo(data as ListShortDataT);
          clearSelectedItems();
        },
      }
    );
  };

  const submitUpdateInfo = (e: any) => {
    e.preventDefault();
    const titleField = document.querySelector(
      'input[name="title"]'
    ) as HTMLInputElement;
    const comissionField = document.querySelector(
      'input[name="comission"]'
    ) as HTMLInputElement;
    if (titleField.value.length && comissionField.value.length) {
      updateInternalList({
        id: internalInfo.id,
        title: titleField.value,
        comission: +comissionField.value,
        cb: (err) => {
          if (err) {
            return console.error("При создании подразделения произошла ошибка");
          }

          toast.success("Сохранено");
        },
      });
    }
  };

  const handleRemoveTrainees = () => {
    const currentItems = selectedItems.filter((e) =>
      internalInfo.trainees_members.map((t) => t.id).includes(e.id)
    );
    open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить ${
          currentItems.length > 1 ? "сотрудников" : "сотрудника"
        } из обучения?`,
      },
      onConfirm: () => {
        deleteTraineesMembersMutation.mutate(
          {
            id: internalInfo.id,
            members: currentItems.map((e) => e.id),
          },
          {
            onSuccess: (data) => {
              toast.success("Сохранено");
              setInternalInfo(data as ListShortDataT);
              clearSelectedItems();
            },
          }
        );
      },
    });
  };

  const handleAddToGroup = () => {
    const currentItems = selectedItems.filter((e) =>
      internalInfo.trainees_members.map((t) => t.id).includes(e.id)
    );
    open("ConfirmActionModal", {
      btnText: "Добавить",
      modalData: {
        text: `Вы уверены, что хотите добавить ${
          currentItems.length > 1 ? "сотрудников" : "сотрудника"
        } в группу?`,
      },
      onConfirm: () => {
        open("InternalListAddEmployeeToGroup", {
          btnText: "Добавить",
          modalData: {
            id: internalInfo.id,
            members: currentItems,
            groups: internalInfo.process_members,
          },
        });
      },
    });
  };

  const handleRemoveFromGroup = (member: any) => {
    open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить ${member.name} из группы?`,
      },
      onConfirm: () => {
        deleteProcessMemberMutation.mutate(
          {
            id: internalInfo.id,
            memberId: member.id,
          },
          {
            onSuccess: (data: any) => {
              toast.success("Сотрудник удален из группы");
              setInternalInfo(data as ListShortDataT);
            },
          }
        );
      },
    });
  };

  const handleSelectProgramsAllByEmployee = (
    employee: { id: number },
    checked: boolean
  ) => {
    const employeeFind = employeeOptions.find((e) => e.id === employee.id);
    if (employeeFind) {
      employeeFind.programs = employeeFind.programs.map((e) => ({
        ...e,
        checked: checked,
      }));
    }
  };

  const onChangeHandleProgram = (
    checked: boolean,
    program: number,
    employee: number
  ) => {
    const employeeFind = employeeOptions?.find((e) => e.id === employee);
    if (employeeFind !== undefined) {
      const programFind = employeeFind.programs?.find(
        (e: any) => e.id === program
      );
      if (programFind !== undefined) programFind.checked = checked;
    }
  };

  const handleDownloadXml = (e: any) => {
    e.preventDefault();
    const href = e.target.getAttribute("href");
    const hrefArr = href.split("/");
    const title = e.target.getAttribute("data-title")
      ? e.target.getAttribute("data-title")
      : e.target.textContent;
    downloadFileXmlMutation.mutate(
      {
        id: id!,
        filename: hrefArr[hrefArr.length - 1].trim(),
      },
      {
        onSuccess: (blob) => {
          downloadBlob(blob, title);
        },
      }
    );
  };

  const handleDownloadProtocol = (e: any) => {
    e.preventDefault();
    const href = e.target.getAttribute("href");
    const hrefArr = href.split("/");
    downloadFileProtocolMutation.mutate(
      {
        id: id!,
        filename: hrefArr[hrefArr.length - 1].trim(),
      },
      {
        onSuccess: (blob) => {
          downloadBlob(blob, `${e.target.textContent}.xlsx`);
        },
      }
    );
  };

  return (
    <Box style={{ padding: "20px 22px 24px" }}>
      {isLoading || isFetched ? (
        <Loader />
      ) : (
        <>
          <BackButton className={style.backButton} />
          <Text
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginTop: 24,
              marginBottom: 24,
            }}
          >
            {internalInfo.title}
          </Text>
          <Box
            component="form"
            onSubmit={submitUpdateInfo}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              maxWidth: "40vw",
            }}
          >
            <TextField
              fullWidth
              required
              variant="filled"
              name="title"
              defaultValue={internalInfo.title ?? ""}
              label="Название обучения"
              placeholder="Введите..."
            />
            <TextField
              label="Комиссия"
              name="comission"
              variant="filled"
              required
              select
              defaultValue={internalInfo.comission}
            >
              {comissions &&
                comissions.map((comission) => (
                  <MenuItem value={comission.id}>{comission.title}</MenuItem>
                ))}
            </TextField>
            <Button type="submit" className={style.submitInfo}>
              Сохранить
            </Button>
          </Box>
          <Box className={clsx(style.members)}>
            <p className={clsx(style.members_title)}>1. Обучаемые сотрудники</p>
            <div className={styles.createTaskModal__filters}>
              <Search
                value={search.value}
                onChange={search.onChange}
                options={internalInfo.trainees_members}
                getOptionLabel={(employee) => employee.name}
                placeholder="Поиск по имени"
                showDropDown={false}
                className={clsx(
                  style.members__search,
                  style.members__employeesSearch
                )}
              />
              <DropDownLabel
                label="Подразделения"
                list={departmentOptions}
                type="departments"
                handlerChangeFilter={(_, value) => {
                  if (!value) {
                    return setSelectedDepartment(SELECT_ALL.id);
                  }
                  setSelectedDepartment(parseInt(value));
                }}
              />
              <Button
                disabled={!selectedItems.length}
                onClick={handleProcessRequestCreate}
              >
                Сформировать файл
              </Button>
              <Button
                disabled={
                  !selectedItems.length || !internalInfo.process_members.length
                }
                onClick={handleAddToGroup}
              >
                Добавить в группу
              </Button>
              <Button
                disabled={!selectedItems.length}
                onClick={handleRemoveTrainees}
              >
                Удалить из обучаемых
              </Button>
            </div>
            <div className={clsx(style.members_list)}>
              <Table>
                <TableHead>
                  <TableCell width={40}>
                    <Checkbox
                      checked={areAllItemsSelected}
                      onChange={(e) => selectAll(e.target.checked)}
                      color="primary"
                      icon={<CheckboxSVG />}
                      checkedIcon={<CheckboxCheckedSVG />}
                      classes={{
                        disabled: style.members__disabledCheckbox,
                      }}
                    />
                  </TableCell>
                  <TableCell>Сотрудник</TableCell>
                  <TableCell>Пр-мы обучения</TableCell>
                  <TableCell>№ протокола</TableCell>
                </TableHead>
                <TableBody>
                  {employeeOptions.map((employee, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Checkbox
                          checked={!!getIsItemSelected(employee)}
                          onChange={(e) => {
                            handleSelectProgramsAllByEmployee(
                              employee,
                              e.target.checked
                            );
                            rerender(employee);
                          }}
                          color="primary"
                          icon={<CheckboxSVG />}
                          checkedIcon={<CheckboxCheckedSVG />}
                          // disabled={!isEditable}
                          classes={{
                            disabled: style.members__disabledCheckbox,
                          }}
                        />
                      </TableCell>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>
                        {employee.programs &&
                          employee.programs.map((p: any, t: number) => (
                            <FormControlLabel
                              key={p.id}
                              control={
                                <Checkbox
                                  checked={p.checked}
                                  name="program"
                                  color="primary"
                                  icon={<CheckboxSVG />}
                                  checkedIcon={<CheckboxCheckedSVG />}
                                  onChange={(event) => {
                                    onChangeHandleProgram(
                                      event.target.checked,
                                      p.id,
                                      employee.id
                                    );
                                    rerender(employee);
                                  }}
                                />
                              }
                              label={p.title}
                              labelPlacement="end"
                              style={
                                employee.programs?.length !== undefined &&
                                t !== employee.programs?.length - 1
                                  ? {
                                      borderBottom: "1px solid #e0e0e0",
                                      marginBottom: 5,
                                      paddingBottom: 5,
                                      width: "100%",
                                    }
                                  : { width: "100%" }
                              }
                            />
                          ))}
                      </TableCell>
                      <TableCell sx={{ minWidth: "300px" }}>
                        <TextField
                          label="№ протокола"
                          name="protocol_number"
                          variant="filled"
                          required
                          defaultValue={employee.protocol_number}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Box>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <p className={clsx(style.members_title)}>2. Файлы для МинТруда</p>
              <div className={clsx(style.members_process)}>
                {internalInfo.process_members.length ? (
                  internalInfo.process_members.map((e, i) => (
                    <div className={clsx(style.members_process_member)}>
                      {e.members &&
                        e.members.map((employee) => (
                          <p>
                            {employee.name} - {employee.protocol_number}
                            <Tooltip title="Удалить" placement="top">
                              <button
                                onClick={() => handleRemoveFromGroup(employee)}
                              >
                                <XIcon size={15} />
                              </button>
                            </Tooltip>
                          </p>
                        ))}
                      <Link
                        href={e.file}
                        onClick={handleDownloadXml}
                        data-title={`xml_${internalInfo.id}_${i}`}
                      >
                        Скачать xml протокол
                      </Link>
                    </div>
                  ))
                ) : (
                  <p className={clsx(style.members_not_files)}>
                    Файлы не сформированы
                  </p>
                )}
              </div>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <p className={clsx(style.members_title)}>3. Итоговые протоколы</p>
              <div className={clsx(style.members_finish)}>
                {internalInfo.finish_members.length ? (
                  internalInfo.finish_members.map((e) => (
                    <div className={clsx(style.members_finish_cont)}>
                      {e.groups &&
                        e.groups.map((group) => (
                          <div
                            className={clsx(style.members_finish_cont_member)}
                          >
                            <p>
                              {group.title} - {group.protocol_number}
                            </p>
                            <ul>
                              {group.members &&
                                group.members.map((m) => (
                                  <li>
                                    {m.name} | {m.id_record}
                                  </li>
                                ))}
                            </ul>
                            <Link
                              href={group.file.path}
                              onClick={handleDownloadProtocol}
                            >
                              {group.file.title}
                            </Link>
                          </div>
                        ))}
                      <Link href={e.file.path} onClick={handleDownloadXml}>
                        {e.file.title}
                      </Link>
                    </div>
                  ))
                ) : (
                  <p className={clsx(style.members_not_files)}>
                    Протоколы не сформированы
                  </p>
                )}
              </div>
              {!!internalInfo.process_members?.length && (
                <Button
                  disabled={!internalInfo.process_members?.length}
                  onClick={handleFinishRequestCreate}
                >
                  Загрузить итоговый протокол
                </Button>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
});

export default InternalListPage;
